import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useApiCountriesGetCollection, useApiCompaniesIdPatch } from '@r/api/generated/client.ts';
import type { CompanyCompanyReadModel } from '@r/api/generated/client.schemas.ts';
import type { ApiCountry } from '@ui/country-dropdown.tsx';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { Save, CalendarDays } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { format } from 'date-fns';
import type { Option } from '@r/types/index.ts';
import { CountryDropdown } from '@ui/country-dropdown.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface UpdateCompanyClientFormProps {
  company: CompanyCompanyReadModel | null;
}

export const UpdateCompanyClientForm: FC<UpdateCompanyClientFormProps> = ({ company }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const [selectedCountryOption, setSelectedCountryOption] = useState<Option | null>(null);

  const {
    data: countriesResponse,
    isLoading: isCountriesLoading,
    isError: isCountriesError
  } = useApiCountriesGetCollection();

  const countries: ApiCountry[] = (countriesResponse?.data || []) as unknown as ApiCountry[];
  const currentCountryAlpha3 = company?.country?.alpha3 || '';

  const FormSchema = z.object({
    name: z.string().min(1, t('form.fields.required')).max(100),
    addressLine1: z
      .string()
      .min(1, t('form.fields.required'))
      .min(7, t('form.error.min', { min: 7 }))
      .max(255)
      .describe(t('form.fields.schema.describe.address.line')),
    addressLine2: z
      .union([z.string(), z.null()])
      .optional()
      .describe(t('form.fields.schema.describe.address.line')),
    addressLine3: z
      .union([z.string(), z.null()])
      .optional()
      .describe(t('form.fields.schema.describe.address.line')),
    zipcode: z
      .string()
      .min(1, t('form.fields.required'))
      .regex(/^\d+$/, t('form.error.invalid.number'))
      .min(4, t('form.error.min', { min: 4 }))
      .max(20)
      .describe(t('form.fields.schema.describe.zipcode')),
    city: z
      .string()
      .min(1, t('form.fields.required'))
      .max(100)
      .describe(t('form.fields.schema.describe.city')),
    countryAlpha3: z.string().min(1, t('form.fields.required'))
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: company?.name || '',
      addressLine1: company?.addressLine1 || '',
      addressLine2: company?.addressLine2 || null,
      addressLine3: company?.addressLine3 || null,
      zipcode: company?.zipcode || '',
      city: company?.city || '',
      countryAlpha3: currentCountryAlpha3
    }
  });

  useEffect(() => {
    if (countries.length > 0 && company?.country) {
      const countryMatch = countries.find((country) => country.alpha3 === company.country?.alpha3);

      if (countryMatch) {
        const initialOption = {
          label: countryMatch.name,
          value: countryMatch.alpha3
        };

        setSelectedCountryOption(initialOption);
        form.setValue('countryAlpha3', initialOption.value);
      }
    }
  }, [countries, company?.country, form]);

  const companyMutation = useApiCompaniesIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('form.company.informations.success'));
      },
      onError: (error) => {
        showOrvalErrorToast(
          error,
          t('form.error.message', {
            errorMessage: t('form.company.informations.error')
          })
        );
      }
    }
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!company) return;
    companyMutation.mutate({ id: String(company.id), data });
  }

  if (!company) {
    return null;
  }

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mx-auto space-y-10 w-full 2xl:w-8/12">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem className="flex flex-col">
              <FormControl>
                <Input
                  placeholder={t('label.company.name')}
                  {...field}
                  error={!!fieldState.error}
                  className="text-3xl font-bold"
                />
              </FormControl>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>
          )}
        />

        <div className="space-y-5">
          <div className="border-b-2 border-muted-foreground/40 pb-1.5 text-muted-foreground/80 font-medium">
            {t('account.company.legal.information.title')}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="unique-code-readonly" className="text-sm font-medium">
                {t('label.company.unique.code')}
              </label>
              <Input
                id="unique-code-readonly"
                value={company.uniqueCode || ''}
                readOnly
                className="cursor-not-allowed bg-muted"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="siren-readonly" className="text-sm font-medium">
                {t('label.company.siren')}
              </label>
              <Input
                id="siren-readonly"
                value={company.siren || ''}
                readOnly
                className="cursor-not-allowed bg-muted"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="tva-readonly" className="text-sm font-medium">
                {t('label.company.tva')}
              </label>
              <Input
                id="tva-readonly"
                value={company.tva || ''}
                readOnly
                className="cursor-not-allowed bg-muted"
              />
            </div>

            <div className="justify-center text-sm">
              <h3 className="mb-2 font-medium">{t('label.company.created.at')}</h3>
              <div className="flex items-center py-4">
                <CalendarDays className="mx-2 w-5 h-5" />
                <span>
                  {t('company.created.at', {
                    createdAt: format(new Date(company.createdAt), 'dd/MM/yyyy')
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="border-b-2 border-muted-foreground/40 pb-1.5 text-muted-foreground/80 font-medium">
            {t('account.company.address.title')}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="addressLine1"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.address.line.1')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.address.line.1')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="zipcode"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.zipcode')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.zipcode')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="addressLine2"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.address.line.2')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.address.line.2')}
                      {...field}
                      value={field.value ?? ''}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.city')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.city')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="addressLine3"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.address.line.3')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.address.line.3')}
                      {...field}
                      value={field.value ?? ''}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            {!isCountriesError && (
              <FormField
                control={form.control}
                name="countryAlpha3"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('label.company.country')}</FormLabel>
                    <CountryDropdown
                      placeholder={t('label.company.country')}
                      defaultValue={selectedCountryOption?.value}
                      loading={isCountriesLoading}
                      options={countries}
                      onChange={(country) => {
                        field.onChange(country.alpha3);
                      }}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>

        <Button loading={companyMutation.isPending} className="w-full" type="submit">
          <Save className="mr-2 w-4 h-4" />
          {t('form.save.modifications')}
        </Button>
      </form>
    </Form>
  );
};
